import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Loader2, Play, Square, Database, AlertCircle, CheckCircle2, Clock, RotateCcw, Activity, LineChart } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { api } from "../../../services/api";

export function StockMigrationControl() {
  const { showToast } = useToast();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [dateRange, setDateRange] = useState({
    start: "2004-01-01",
    end: new Date().toISOString().split('T')[0] || new Date().toISOString().substring(0, 10)
  });
  const [envConfig, setEnvConfig] = useState({
    token: "",
    workers: 5,
    rps: 5
  });

  const loadStatus = async () => {
    try {
      const result = await api.getStockMigrationStatus();
      if (result.success && result.data) {
        setStatus(result.data);
        if (result.data.config) {
            setEnvConfig({
                token: result.data.config.current_token || "",
                workers: result.data.config.max_workers || 5,
                rps: result.data.config.max_rps || 5
            });
        }
      }
    } catch (err: any) {
      console.error("Failed to load stock migration status:", err);
    }
  };

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSeed = async () => {
    if (!window.confirm(`Yakin ingin melakukan seeding emiten? Ini akan mengulang status antrean.`)) return;
    
    setActionLoading('seed');
    try {
      const result = await api.seedStockMigration(dateRange.start || '', dateRange.end || '');
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Seeding Berhasil',
          message: `Berhasil menyiapkan ${result.data?.count || 0} saham ke antrean.`
        });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Seeding Gagal', message: result.error || 'Terjadi kesalahan' });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (action: 'start' | 'stop') => {
    setActionLoading(action);
    try {
      const result = action === 'start' ? await api.startStockMigration() : await api.stopStockMigration();
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Berhasil',
          message: `Migrasi Saham berhasil ${action === 'start' ? 'dijalankan' : 'dihentikan'}`
        });
        loadStatus();
      } else {
        showToast({ type: 'error', title: 'Gagal', message: result.error || `Gagal ${action} migrasi` });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateConfig = async () => {
    setActionLoading('config');
    try {
      const result = await api.updateStockMigrationConfig({ 
          currentToken: envConfig.token, 
          maxWorkers: envConfig.workers, 
          maxRps: envConfig.rps 
      });
      if (result.success) {
        showToast({ type: 'success', title: 'Config Diupdate', message: 'Environment Config berhasil diperbarui' });
      } else {
        showToast({ type: 'error', title: 'Gagal', message: result.error || 'Gagal update config' });
      }
    } catch (err: any) {
      showToast({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const isRunning = status?.config?.is_running;
  const summary = status?.summary || [];
  
  const getCount = (s: string) => summary.find((item: any) => item.status === s)?.total_tasks || 0;
  const total = summary.reduce((acc: number, item: any) => acc + parseInt(item.total_tasks), 0);
  const completed = getCount('COMPLETED');
  const failed = getCount('FAILED');
  const skipped = getCount('SKIPPED');

  const progressPercent = total > 0 ? Math.round(((completed + skipped) / total) * 100) : 0;

  return (
    <Card className="mb-6 overflow-hidden border-t-4 border-t-sky-600 shadow-lg bg-[#0f172a] text-slate-200">
      <CardHeader className="bg-slate-900/50 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-500/10 rounded-lg">
              <LineChart className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Stock OHLCV Migration</CardTitle>
              <CardDescription className="text-slate-400">Migrate Historical Chartbit Data (One-Shot per Stock)</CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${isRunning ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`}>
              {isRunning ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              {isRunning ? 'MIGRATING' : 'IDLE'}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-sky-400">
              {progressPercent}% Complete
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Progress Bar */}
        <div className="w-full bg-slate-800 h-3 rounded-full mb-6 overflow-hidden border border-slate-700">
          <div 
            className="bg-sky-500 h-full transition-all duration-1000 ease-in-out shadow-[0_0_10px_rgba(14,165,233,0.5)]" 
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Side: Stats & Config */}
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/50 text-center">
                <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">Total Stocks</p>
                <p className="text-lg font-mono text-white">{total.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-500/5 rounded-xl border border-green-500/20 text-center">
                <p className="text-[10px] text-green-400 uppercase font-bold mb-1">Success / Skip</p>
                <p className="text-lg font-mono text-green-400">{(completed + skipped).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-500/5 rounded-xl border border-red-500/20 text-center">
                <p className="text-[10px] text-red-400 uppercase font-bold mb-1">Failed</p>
                <p className="text-lg font-mono text-red-400">{failed.toLocaleString()}</p>
              </div>
            </div>

            <div className="p-4 bg-slate-900/40 rounded-xl border border-slate-700 space-y-4">
              <p className="text-xs font-bold text-slate-300 uppercase flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-sky-400" />
                Stock Environment Config
              </p>
              
              <div className="space-y-1.5">
                <label className="text-[10px] text-slate-500 uppercase">Stockbit Token (Bearer)</label>
                <Input 
                  type="text" 
                  value={envConfig.token} 
                  onChange={e => setEnvConfig({...envConfig, token: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-xs h-9 font-mono"
                  placeholder="Masukkan token Bearer Stockbit..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">Max Workers</label>
                  <Input 
                    type="number" 
                    value={envConfig.workers} 
                    onChange={e => setEnvConfig({...envConfig, workers: parseInt(e.target.value) || 1})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                    min="1"
                    max="20"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">Max RPS</label>
                  <Input 
                    type="number" 
                    value={envConfig.rps} 
                    onChange={e => setEnvConfig({...envConfig, rps: parseInt(e.target.value) || 1})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                    min="1"
                    max="20"
                  />
                </div>
              </div>
              <Button 
                onClick={handleUpdateConfig}
                disabled={actionLoading === 'config'}
                className="w-full bg-sky-600 hover:bg-sky-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
              >
                {actionLoading === 'config' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : "Save Config"}
              </Button>
            </div>

            <div className="p-4 bg-slate-900/40 rounded-xl border border-slate-700 space-y-4">
              <p className="text-xs font-bold text-slate-300 uppercase flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-sky-400" />
                Stock Dates Seeding
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">Start Date</label>
                  <Input 
                    type="date" 
                    value={dateRange.start} 
                    onChange={e => setDateRange({...dateRange, start: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-500 uppercase">End Date</label>
                  <Input 
                    type="date" 
                    value={dateRange.end} 
                    onChange={e => setDateRange({...dateRange, end: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-xs h-9"
                  />
                </div>
              </div>
              <Button 
                onClick={handleSeed}
                disabled={actionLoading === 'seed' || isRunning}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-9 rounded-lg text-xs font-bold transition-all active:scale-95"
              >
                {actionLoading === 'seed' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <RotateCcw className="w-3.5 h-3.5 mr-2" />}
                Re-Seed Stocks
              </Button>
              <p className="text-[10px] text-slate-500 italic">
                * Menyiapkan {`~`}967 emiten ke antrean. Start/End date akan dikirim ke backend tetapi worker OHLCV menggunakan One-Shot bulk fetch dari tanggal IPO.
              </p>
            </div>
          </div>

          {/* Right Side: Actions & Workers */}
          <div className="space-y-6">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={() => handleAction('start')} 
                  disabled={isRunning || actionLoading !== null || total === 0}
                  className="h-12 bg-green-600 hover:bg-green-700 text-white border-none rounded-xl transition-all active:scale-95 disabled:opacity-50"
                >
                  {actionLoading === 'start' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Migration
                </Button>
                <Button 
                  onClick={() => handleAction('stop')} 
                  disabled={!isRunning || actionLoading !== null}
                  variant="destructive"
                  className="h-12 bg-red-600 hover:bg-red-700 text-white border-none rounded-xl transition-all active:scale-95 disabled:opacity-50"
                >
                  {actionLoading === 'stop' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Square className="w-4 h-4 mr-2" />}
                  Stop Workers
                </Button>
              </div>
              
              <div className="p-3 bg-slate-800/30 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-xs text-slate-400">Target Workers</span>
                <span className="text-sm font-mono text-sky-400 font-bold">{status?.config?.max_workers || 0}</span>
              </div>

              {/* Active Workers Tasks */}
              {status?.activeTasks?.length > 0 ? (
                <div className="space-y-2 pt-2">
                  <p className="text-[10px] font-bold text-green-400 uppercase flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5 animate-pulse" />
                    Worker Activity
                  </p>
                  <div className="max-h-[300px] overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-slate-700 pr-1">
                    {status.activeTasks.map((task: any, idx: number) => (
                      <div key={idx} className="text-[10px] p-2 bg-green-500/5 border border-green-500/10 rounded-lg flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-green-500/20 rounded text-[9px] text-green-300 font-mono">{task.worker_id}</span>
                          <span className="text-white font-semibold">{task.stock_code}</span>
                        </div>
                        <span className="text-slate-500">Fetching Data...</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center border border-dashed border-slate-700 rounded-xl opacity-50">
                    <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No active workers processing</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
